import {
    Search,
    X,
    ZoomIn,
    ZoomOut,
    Move
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const InvestmentZonesMap = ({
    // Données reçues du parent
    selectedZones = [],
    loading = false,
    error = null,
    saving = false,
    
    // Handlers pour les modifications
    onZonesChange,
    
    // Props existantes
    userLocation = { lat: 48.8566, lng: 2.3522 } // Paris par défaut
}) => {
    // États locaux pour l'UI seulement
    const [searchTerm, setSearchTerm] = useState('');
    const [zoomLevel, setZoomLevel] = useState(1.2);
    const [showFilters, setShowFilters] = useState(false);
    const [geoData, setGeoData] = useState(null);
    const [loadingGeo, setLoadingGeo] = useState(true);
    const [hoveredDept, setHoveredDept] = useState(null);
    
    // États pour le déplacement de la carte
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [mapOffset, setMapOffset] = useState({ x: 0, y: 0 });
    const [isMapMode, setIsMapMode] = useState(false);
    
    const mapRef = useRef(null);
    const svgRef = useRef(null);

    // Chargement des données GeoJSON
    useEffect(() => {
        fetch("/data/cartes_geographiques/departements_small.geojson")
            .then((res) => res.json())
            .then((data) => {
                setGeoData(data);
                setLoadingGeo(false);
            })
            .catch(() => {
                setGeoData(null);
                setLoadingGeo(false);
            });
    }, []);

    // Gestionnaire pour le zoom avec la roulette de la souris
    const handleWheel = (e) => {
        e.preventDefault();
        
        const delta = e.deltaY;
        const zoomFactor = 0.1;
        
        setZoomLevel(prev => {
            const newLevel = delta > 0 
                ? Math.max(prev - zoomFactor, 0.5)  // Zoom arrière
                : Math.min(prev + zoomFactor, 3);   // Zoom avant
            return newLevel;
        });
    };

    // Gestionnaires d'événements pour le déplacement
    const handleMouseDown = (e) => {
        if (!isMapMode) return;
        
        setIsDragging(true);
        setDragStart({
            x: e.clientX - mapOffset.x,
            y: e.clientY - mapOffset.y
        });
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !isMapMode) return;
        
        const newOffset = {
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        };
        
        setMapOffset(newOffset);
        e.preventDefault();
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Event listeners pour le déplacement
    useEffect(() => {
        if (isMapMode) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, isMapMode, dragStart, mapOffset]);

    // Event listener pour le zoom avec la roulette
    useEffect(() => {
        const mapElement = mapRef.current;
        if (mapElement) {
            const handleWheelWithStop = (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleWheel(e);
            };
            
            mapElement.addEventListener('wheel', handleWheelWithStop, { passive: false });
            
            return () => {
                mapElement.removeEventListener('wheel', handleWheelWithStop);
            };
        }
    }, []);

    // Réinitialiser la position de la carte
    const resetMapPosition = () => {
        setMapOffset({ x: 0, y: 0 });
        setZoomLevel(1.2);
    };

    // Projection des coordonnées géographiques vers SVG
    const projectToSVG = (coordinates) => {
        if (!geoData) return [];

        const bounds = {
            minLng: -5.5,
            maxLng: 10,
            minLat: 41,
            maxLat: 52
        };

        const width = 700;
        const height = 500;

        return coordinates.map(coord => {
            if (Array.isArray(coord[0]) && Array.isArray(coord[0][0])) {
                return coord.map(ring =>
                    ring.map(point => {
                        const lng = point[0];
                        const lat = point[1];
                        return [
                            ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * width,
                            height - ((lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * height
                        ];
                    })
                );
            } else {
                return coord.map(point => {
                    const lng = point[0];
                    const lat = point[1];
                    return [
                        ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * width,
                        height - ((lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * height
                    ];
                });
            }
        });
    };

    // Conversion des coordonnées en path SVG
    const coordinatesToPath = (coordinates) => {
        const projected = projectToSVG(coordinates);

        if (!projected.length) return '';

        let pathData = '';

        projected.forEach((ring, index) => {
            if (Array.isArray(ring[0]) && Array.isArray(ring[0][0])) {
                ring.forEach((subRing, subIndex) => {
                    if (subRing.length > 0) {
                        pathData += `M ${subRing[0][0]},${subRing[0][1]} `;
                        pathData += subRing.slice(1).map(point => `L ${point[0]},${point[1]}`).join(' ');
                        pathData += ' Z ';
                    }
                });
            } else {
                if (ring.length > 0) {
                    pathData += `M ${ring[0][0]},${ring[0][1]} `;
                    pathData += ring.slice(1).map(point => `L ${point[0]},${point[1]}`).join(' ');
                    pathData += ' Z ';
                }
            }
        });

        return pathData;
    };

    // Filtrage des départements
    const getFilteredFeatures = () => {
        if (!geoData) return [];

        return geoData.features.filter(feature => {
            const deptCode = feature.properties.code;
            const deptName = feature.properties.nom;

            const matchesSearch = searchTerm === '' ||
                deptName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                deptCode.includes(searchTerm);

            return matchesSearch;
        });
    };

    const handleZoneToggle = (deptCode, isCtrlClick = false) => {
        if (isMapMode) return; // Désactiver la sélection en mode déplacement
        
        let newSelectedZones;

        if (isCtrlClick) {
            // Sélection multiple avec Ctrl
            if (selectedZones.includes(deptCode)) {
                newSelectedZones = selectedZones.filter(id => id !== deptCode);
            } else {
                newSelectedZones = [...selectedZones, deptCode];
            }
        } else {
            // Sélection simple
            if (selectedZones.includes(deptCode)) {
                newSelectedZones = selectedZones.filter(id => id !== deptCode);
            } else {
                newSelectedZones = [...selectedZones, deptCode];
            }
        }

        if (onZonesChange) {
            onZonesChange(newSelectedZones);
        }
    };

    const handleRemoveZone = (deptCode, e) => {
        e.stopPropagation();
        const newSelectedZones = selectedZones.filter(id => id !== deptCode);
        if (onZonesChange) {
            onZonesChange(newSelectedZones);
        }
    };

    const handleZoom = (direction) => {
        setZoomLevel(prev => {
            const newLevel = direction === 'in' ? Math.min(prev + 0.2, 3) : Math.max(prev - 0.2, 0.5);
            return newLevel;
        });
    };

    // Style des départements
    const getDepartmentStyle = (deptCode, isHovered) => {
        const isSelected = selectedZones.includes(deptCode);

        let style = {
            fill: "#E5E7E9",
            stroke: "white",
            strokeWidth: 1,
            strokeDasharray: "1",
            fillOpacity: 0.7,
            cursor: isMapMode ? "move" : "pointer"
        };

        if (isSelected) {
            style.fill = "#bed1d0";
            style.fillOpacity = 0.9;
            style.stroke = "#9CA3AF";
            style.strokeWidth = 3;
            style.strokeDasharray = "none";
        }

        if (isHovered && !isMapMode) {
            style.strokeWidth = 5;
            style.stroke = "#6B7280";
            style.fillOpacity = 0.9;
        }

        return style;
    };

    // Affichage du loading
    if (loading || loadingGeo) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">
                        {loading ? 'Chargement des préférences...' : 'Chargement de la carte...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col shadow-sm">
            {/* Header */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-slate-700">
                        <h2 className="text-sm font-semibold">Zones d'Intervention</h2>
                    </div>

                    {/* Indicateur de sauvegarde */}
                    {saving && (
                        <span className="text-xs text-green-600 font-medium">
                            Sauvegarde...
                        </span>
                    )}
                </div>
            </div>

            {/* Affichage d'erreur */}
            {error && (
                <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-red-500 mr-2 flex-shrink-0"></div>
                        <span className="text-sm text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Contenu principal avec layout horizontal */}
            <div className="flex-1 overflow-hidden flex">
                {/* Section gauche - Carte et contrôles */}
                <div className="flex-1 overflow-y-auto p-4 space-y-1">
                    {/* Barre de recherche avec contrôles de zoom */}
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Rechercher département..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                            />

                            {/* Suggestions de départements */}
                            {searchTerm && geoData && getFilteredFeatures().length > 0 && searchTerm.length > 1 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                                    {getFilteredFeatures().slice(0, 8).map((feature) => {
                                        const deptCode = feature.properties.code;
                                        const deptName = feature.properties.nom;
                                        const isSelected = selectedZones.includes(deptCode);

                                        return (
                                            <div
                                                key={deptCode}
                                                className="flex items-center justify-between px-3 py-2 hover:bg-emerald-50 cursor-pointer text-xs border-b border-gray-100 last:border-b-0"
                                                onClick={() => {
                                                    handleZoneToggle(deptCode);
                                                    setSearchTerm('');
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-slate-800">{deptName}</span>
                                                    <span className="text-slate-500">({deptCode})</span>
                                                </div>
                                                {isSelected && (
                                                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-0.5">
                            <button
                                onClick={() => handleZoom('in')}
                                className="p-1.5 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                                title="Zoom avant"
                            >
                                <ZoomIn className="w-3.5 h-3.5 text-slate-600" />
                            </button>
                            <button
                                onClick={() => handleZoom('out')}
                                className="p-1.5 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                                title="Zoom arrière"
                            >
                                <ZoomOut className="w-3.5 h-3.5 text-slate-600" />
                            </button>
                            <button
                                onClick={() => setIsMapMode(!isMapMode)}
                                className={`p-1.5 border border-gray-300 rounded-lg shadow-sm transition-colors ${
                                    isMapMode 
                                        ? 'bg-emerald-100 border-emerald-300 text-emerald-700' 
                                        : 'bg-white hover:bg-emerald-50 hover:border-emerald-300 text-slate-600'
                                }`}
                                title={isMapMode ? "Mode sélection" : "Mode déplacement"}
                            >
                                <Move className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={resetMapPosition}
                                className="p-1.5 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors text-xs px-2"
                                title="Réinitialiser la position"
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    {/* Indicateur de mode */}
                    {isMapMode && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs text-emerald-700">
                            <div className="flex items-center gap-2">
                                <Move className="w-3 h-3" />
                                Mode déplacement activé - Cliquez et faites glisser pour déplacer la carte
                            </div>
                        </div>
                    )}

                    <div className="relative bg-white overflow-hidden" style={{ height: '450px' }}>
                        {/* Carte SVG avec départements GeoJSON */}
                        {geoData && (
                            <>
                                <div
                                    ref={mapRef}
                                    className="relative p-3 h-full"
                                    style={{ 
                                        transform: `scale(${zoomLevel}) translate(${mapOffset.x}px, ${mapOffset.y}px)`,
                                        transformOrigin: 'center center',
                                        cursor: isMapMode ? (isDragging ? 'grabbing' : 'grab') : 'default'
                                    }}
                                    onMouseDown={handleMouseDown}
                                >
                                    <svg
                                        ref={svgRef}
                                        viewBox="0 0 700 500"
                                        className="w-full h-full"
                                        preserveAspectRatio="xMidYMid meet"
                                        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
                                    >
                                        {getFilteredFeatures().map((feature) => {
                                            const deptCode = feature.properties.code;
                                            const deptName = feature.properties.nom;
                                            const isHovered = hoveredDept === deptCode && !isMapMode;
                                            const pathData = coordinatesToPath(feature.geometry.coordinates);
                                            const style = getDepartmentStyle(deptCode, isHovered);

                                            return (
                                                <path
                                                    key={deptCode}
                                                    d={pathData}
                                                    fill={style.fill}
                                                    stroke={style.stroke}
                                                    strokeWidth={style.strokeWidth}
                                                    strokeDasharray={style.strokeDasharray}
                                                    fillOpacity={style.fillOpacity}
                                                    className="transition-all duration-200"
                                                    style={{ cursor: style.cursor }}
                                                    onClick={(e) => handleZoneToggle(deptCode, e.ctrlKey || e.metaKey)}
                                                    onMouseEnter={() => !isMapMode && setHoveredDept(deptCode)}
                                                    onMouseLeave={() => setHoveredDept(null)}
                                                />
                                            );
                                        })}
                                    </svg>
                                </div>

                                {/* Tooltip pour le département survolé */}
                                {hoveredDept && !isMapMode && (
                                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-300 z-20">
                                        <div className="text-sm font-medium text-slate-800">
                                            {geoData.features.find(f => f.properties.code === hoveredDept)?.properties.nom} ({hoveredDept})
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Erreur de chargement */}
                        {!loadingGeo && !geoData && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                                <div className="text-sm text-red-600">Erreur lors du chargement de la carte</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section droite - Liste des départements sélectionnés */}
                {selectedZones.length > 0 && geoData && (
                    <div className="w-56 border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
                        {/* En-tête fixe */}
                        <div className="bg-gray-50 border-b border-gray-200 p-4 flex-shrink-0">
                            <h3 className="text-xs font-semibold text-slate-700">
                                Départements sélectionnés ({selectedZones.length})
                            </h3>
                        </div>

                        {/* Liste scrollable */}
                        <div className="flex-1 overflow-y-auto p-3 pt-2">
                            <div className="space-y-1">
                                {selectedZones
                                    .map(deptCode => {
                                        const feature = geoData.features.find(f => f.properties.code === deptCode);
                                        const deptName = feature?.properties.nom || `Département ${deptCode}`;
                                        return { deptCode, deptName };
                                    })
                                    .sort((a, b) => a.deptName.localeCompare(b.deptName))
                                    .map(({ deptCode, deptName }) => (
                                        <div
                                            key={deptCode}
                                            className="flex items-center justify-between bg-white border border-gray-300 rounded-lg p-2 hover:bg-gray-50 transition-all duration-200 group"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium text-slate-800 truncate">
                                                    {deptName} ({deptCode})
                                                </div>
                                            </div>
                                            <X
                                                className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2 hover:text-red-500 cursor-pointer"
                                                onClick={(e) => handleRemoveZone(deptCode, e)}
                                                title="Retirer ce département"
                                            />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvestmentZonesMap;